import {
  claveDeBloqueDwg,
  claveDeCapaDwg,
  claveDeClaseIfc,
  claveDeTipoIfc,
  resolverTipo,
  type AsignacionDelPlano,
  type ClaveDeTipo,
  type PlanoCapa,
  type EstadoDeLinea,
  type Receta,
  type TiposDelPlanoResponse,
  type ReconocerTiposResponse,
  type ReconocimientoDeTipo,
  type Resolucion,
} from '@supernova/shared';
import { estadoDeCandidata } from './tiposDelDialogo.js';

/**
 * «Conceptos por tipo» en el armado: lógica PURA (sin red ni estado) para decidir, por elemento, si lo decide la
 * memoria / el plano o el motor de siempre. Con la memoria vacía todas las resoluciones son `motor` y el armado
 * no cambia (prueba W1).
 */

export interface ClavesDelElemento {
  tipo: ClaveDeTipo | null;
  clase: ClaveDeTipo | null;
}

/** Las claves de un candidato del armado (`capa:<nombre>` / `bloque:<nombre>`). IFC: el tipo del modelo y su clase. */
export function clavesDelElemento(
  key: string,
  esIfc: boolean,
  capaInfo: PlanoCapa | undefined,
  materialesDeducidos?: Parameters<typeof claveDeTipoIfc>[1],
): ClavesDelElemento {
  if (esIfc) {
    if (!capaInfo) return { tipo: null, clase: null };
    return { tipo: claveDeTipoIfc(capaInfo, materialesDeducidos), clase: claveDeClaseIfc(capaInfo) };
  }
  const i = key.indexOf(':');
  const tipo = key.slice(0, i);
  const nombre = key.slice(i + 1);
  if (!nombre) return { tipo: null, clase: null };
  return { tipo: tipo === 'bloque' ? claveDeBloqueDwg(nombre) : claveDeCapaDwg(nombre), clase: null };
}

/**
 * El servidor juzga «catálogo marcado» con los catálogos GUARDADOS del proyecto, pero la pantalla puede estar usando uno
 * que propuso y el proyecto aún no guardó (ECOSTOS por omisión). Lo que se usa es lo que la pantalla usa: se corrige
 * solo esa diferencia (un catálogo que la pantalla usa no está «sin marcar»).
 */
export function ajustarMarcados<T extends { reconocer: ReconocerTiposResponse | null; plano: TiposDelPlanoResponse | null }>(
  mem: T,
  usados: ReadonlySet<string>,
): T {
  return {
    ...mem,
    reconocer: mem.reconocer
      ? {
          ...mem.reconocer,
          tipos: mem.reconocer.tipos.map((t) => ({
            ...t,
            candidatas: t.candidatas.map((c) => ({
              ...c,
              lineas: c.lineas.map((l, i) => ({ ...l, estado: estadoDeCandidata(c, i, usados) })),
            })),
          })),
        }
      : null,
    // Estas líneas no traen su foto: solo se corrige el «no marcado» (para aplicar basta que el concepto exista hoy).
    plano: mem.plano
      ? { ...mem.plano, resueltos: mem.plano.resueltos.map((x) => ({ ...x, estado: x.estado === 'catalogo_no_marcado' && usados.has(x.catalogId) ? ('vigente' as const) : x.estado })) }
      : null,
  };
}

/** El reconocimiento de la memoria para una clave (misma canónica y uso). */
export function reconocimientoDe(resp: ReconocerTiposResponse | null, clave: ClaveDeTipo | null): ReconocimientoDeTipo | null {
  if (!resp || !clave) return null;
  return resp.tipos.find((t) => t.canonica === clave.canonica && t.uso === clave.uso) ?? null;
}

export function resolverElemento(
  claves: ClavesDelElemento,
  delPlano: readonly AsignacionDelPlano[],
  reconocimiento: ReconocerTiposResponse | null,
  /** Piezas del tipo (IFC: GlobalId; DWG: id de la inserción): con ellas salen las excepciones por pieza. */
  gidsDelTipo: readonly string[] = [],
): Resolucion {
  return resolverTipo({
    claveTipo: claves.tipo,
    claveClase: claves.clase,
    gidsDelTipo: [...gidsDelTipo],
    delPlano,
    reconocimiento: reconocimientoDe(reconocimiento, claves.tipo),
  });
}

/** Una pieza de un tipo: su id en el visor (expressID / id de la inserción) y su identificador estable (`gid`). */
export interface PiezaDelTipo<I extends number | string = number> {
  id: I;
  gid: string;
}

/**
 * Parte un tipo según las piezas con concepto propio (excepciones): qué piezas se sacan del tipo y cada grupo con su receta.
 * Un identificador que el modelo ya no trae no cuenta (la pieza se fue en una revisión).
 */
export function particionarPorExcepciones<I extends number | string>(
  res: Resolucion,
  piezas: readonly PiezaDelTipo<I>[],
): { excluirDelTipo: Set<I>; grupos: { ids: I[]; receta: Receta }[] } {
  const idDe = new Map(piezas.map((p) => [p.gid, p.id] as const));
  const excluirDelTipo = new Set<I>();
  const grupos: { ids: I[]; receta: Receta }[] = [];
  for (const g of res.excepciones) {
    const ids = g.gids.map((gid) => idDe.get(gid)).filter((x): x is I => x !== undefined);
    if (ids.length === 0) continue;
    ids.forEach((id) => excluirDelTipo.add(id));
    grupos.push({ ids, receta: g.receta });
  }
  return { excluirDelTipo, grupos };
}

/** Nota de origen del renglón (§11.4). */
export function notaDeOrigen(r: Resolucion): string {
  if (r.fuente === 'pieza_plano') return 'Lo asignaste tú para estas piezas.';
  if (r.fuente === 'tipo_memoria' && r.memoria) {
    const fecha = new Date(r.memoria.fecha).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
    const donde = r.memoria.deProyecto ? ` en «${r.memoria.deProyecto}»` : '';
    return r.memoria.porOtro ? `Lo asignó ${r.memoria.porOtro}${donde} (${fecha}).` : `Lo asignaste${donde} (${fecha}).`;
  }
  return 'Lo asignaste tú en «Asignar conceptos a elementos».';
}

/** La misma clave puede existir en dos unidades (catálogos reales): la llave lleva la unidad. */
const llaveDeConcepto = (catalogId: string, clave: string, unidad: string | null): string => `${catalogId}|${clave}|${unidad ?? ''}`;

export interface ConceptoResuelto {
  /** Estado de la línea contra el catálogo de hoy: solo 'vigente' se puede aplicar. */
  estado: EstadoDeLinea;
  resourceId: string;
  precio: number | null;
  unidad: string | null;
  descripcion: string | null;
}

/** Cada concepto de las asignaciones del plano y de las recetas reconocidas, contra el catálogo de HOY (lo resolvió el servidor). */
export function conceptosResueltos(
  plano: TiposDelPlanoResponse | null,
  reconocer: ReconocerTiposResponse | null,
): Map<string, ConceptoResuelto> {
  const m = new Map<string, ConceptoResuelto>();
  for (const x of plano?.resueltos ?? []) {
    if (x.resourceId) m.set(llaveDeConcepto(x.catalogId, x.clave, x.unidad), { estado: x.estado, resourceId: x.resourceId, precio: x.precio, unidad: x.unidad, descripcion: x.descripcion });
  }
  for (const t of reconocer?.tipos ?? []) {
    for (const cand of t.candidatas) {
      cand.receta.lineas.forEach((l, i) => {
        const e = cand.lineas[i];
        if (!e?.resourceId) return;
        // El estado ya viene ajustado a los catálogos que la pantalla usa (`ajustarMarcados`). Una entrada vigente no se pisa
        // con una peor (la misma clave puede salir de dos recetas).
        const k = llaveDeConcepto(l.catalogId, l.clave, e.unidadActual);
        const ya = m.get(k);
        if (ya && (ya.estado === 'vigente' || e.estado !== 'vigente')) return;
        m.set(k, { estado: e.estado, resourceId: e.resourceId, precio: e.precio, unidad: e.unidadActual, descripcion: e.descripcionActual });
      });
    }
  }
  return m;
}

/** El concepto de una línea de la receta SI sigue vigente hoy (misma clave y unidad que se recordó); si no, undefined. */
export function resueltoDeLinea(resueltos: ReadonlyMap<string, ConceptoResuelto>, l: Receta['lineas'][number]): ConceptoResuelto | undefined {
  const r = resueltos.get(llaveDeConcepto(l.catalogId, l.clave, l.unidad));
  return r && r.estado === 'vigente' ? r : undefined;
}

/** Un elemento que todavía no tiene principal en la hoja: sus claves y las piezas (identificador estable) que trae. */
export interface ElementoParaEnvio {
  key: string;
  claves: ClavesDelElemento;
  gids: string[];
}

/**
 * Lo que «Poner en la hoja lo asignado» agregaría, sin medir ni consultar nada. Entra SOLO lo que el armado aplicaría solo
 * (receta de la persona o memoria exacta y vigente, entera): lo sugerido, en conflicto o con un concepto que ya no sirve no
 * entra, y una receta nunca entra a medias.
 */
export interface PlanDeEnvio {
  entran: {
    key: string;
    /** El tipo entero (menos las piezas con concepto propio). false = solo entran sus excepciones. */
    base: boolean;
    /** Grupos de piezas con concepto propio que entran como elemento aparte. */
    grupos: number;
    /** Líneas SIN juntar por concepto: principal + complementarios recordados, de la base y de cada grupo. */
    lineas: number;
  }[];
  /** Elementos que entran (cada tipo con su base cuenta uno, y cada grupo de piezas otro). */
  elementos: number;
  principales: number;
  complementarios: number;
  conteos: { sinConcepto: number; noPresupuestar: number; sugeridos: number; conflictos: number; noVigentes: number };
}

export function planDeEnvio(
  els: readonly ElementoParaEnvio[],
  mem: { reconocer: ReconocerTiposResponse | null; plano: TiposDelPlanoResponse | null },
  resueltos: ReadonlyMap<string, ConceptoResuelto>,
): PlanDeEnvio {
  const plan: PlanDeEnvio = {
    entran: [],
    elementos: 0,
    principales: 0,
    complementarios: 0,
    conteos: { sinConcepto: 0, noPresupuestar: 0, sugeridos: 0, conflictos: 0, noVigentes: 0 },
  };
  const lineasDe = (r: Receta) => 1 + (r.recuerda.complementarios ? r.lineas.filter((l) => l.rol === 'complemento').length : 0);
  for (const e of els) {
    const res = resolverElemento(e.claves, mem.plano?.asignaciones ?? [], mem.reconocer, e.gids);
    const cubiertas = new Set<string>();
    let grupos = 0;
    let lineas = 0;
    let principales = 0;
    for (const g of res.excepciones) {
      g.gids.forEach((x) => cubiertas.add(x));
      if (g.receta.noPresupuestar) {
        plan.conteos.noPresupuestar++;
        continue;
      }
      if (!recetaAplicable(g.receta, resueltos)) {
        plan.conteos.noVigentes++;
        continue;
      }
      grupos++;
      principales++;
      lineas += lineasDe(g.receta);
    }
    // El tipo entero: si todas sus piezas tienen concepto propio, no queda nada de él.
    const baseVacia = e.gids.length > 0 && e.gids.every((x) => cubiertas.has(x));
    let base = false;
    if (res.receta) {
      if (res.receta.noPresupuestar) plan.conteos.noPresupuestar++;
      else if (!recetaAplicable(res.receta, resueltos)) plan.conteos.noVigentes++;
      else if (!baseVacia) {
        base = true;
        principales++;
        lineas += lineasDe(res.receta);
      }
    } else if (!baseVacia) {
      if (res.pendiente?.estado === 'sugerencia') plan.conteos.sugeridos++;
      else if (res.pendiente?.estado === 'conflicto') plan.conteos.conflictos++;
      else if (res.pendiente?.estado === 'no_vigente') plan.conteos.noVigentes++;
      else plan.conteos.sinConcepto++;
    }
    if (base || grupos > 0) {
      plan.entran.push({ key: e.key, base, grupos, lineas });
      plan.elementos += (base ? 1 : 0) + grupos;
      plan.principales += principales;
      plan.complementarios += lineas - principales;
    }
  }
  return plan;
}

/** ¿Se puede armar con esta receta? «No presupuestar» siempre; si no, su principal (y sus complementarios, si se recuerdan) siguen vigentes hoy. */
export function recetaAplicable(receta: Receta, resueltos: ReadonlyMap<string, ConceptoResuelto>): boolean {
  if (receta.noPresupuestar) return true;
  const hay = (l: Receta['lineas'][number]) => !!resueltoDeLinea(resueltos, l);
  const principal = receta.lineas.find((l) => l.rol === 'principal');
  if (!principal || !hay(principal)) return false;
  return !receta.recuerda.complementarios || receta.lineas.every(hay);
}
